import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, SLUG } from "@/lib/api";
import SpaceStage from "@/components/space/SpaceStage";
import ThoughtComposer from "@/components/space/ThoughtComposer";
import ClusterDrawer from "@/components/space/ClusterDrawer";
import ManifestationModal from "@/components/space/ManifestationModal";
import { Plus, X, Info } from "lucide-react";

export default function SpacePage() {
  const [space, setSpace] = useState(null);
  const [graph, setGraph] = useState(null);
  const [entered, setEntered] = useState(false);
  const [pulse, setPulse] = useState(null);
  const [clusterId, setClusterId] = useState(null);
  const [manifestation, setManifestation] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [infoSeen, setInfoSeen] = useState(() => sessionStorage.getItem("cs-info-seen") === "1");

  const loadGraph = useCallback(async () => {
    const { data } = await api.get(`/spaces/${SLUG}/graph`);
    setGraph(data);
    return data;
  }, []);

  useEffect(() => {
    api.get(`/spaces/${SLUG}`).then((r) => setSpace(r.data)).catch(() => {});
    loadGraph();
  }, [loadGraph]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mid = params.get("manifestation");
    if (mid) {
      setEntered(true);
      api.get(`/spaces/${SLUG}/manifestations/${mid}`).then((r) => setManifestation(r.data)).catch(() => {});
    }
  }, []);

  const onSubmitted = async (result) => {
    const g = await loadGraph();
    let cid = result.cluster_id;
    if (cid && !g.clusters.find((c) => c.id === cid)) cid = null;
    setPulse({ clusterId: cid, ts: Date.now() });
  };

  const openInfo = () => {
    setAboutOpen((v) => !v);
    setInfoSeen(true);
    sessionStorage.setItem("cs-info-seen", "1");
  };

  const enrichManifestation = (m) => {
    if (!m || !graph?.clusters?.length) return m;
    const themes = new Set((m.themes || []).map((t) => String(t).toLowerCase()));
    let best = null;
    let score = 0;
    graph.clusters.forEach((c) => {
      const shared = (c.themes || []).filter((t) => themes.has(String(t).toLowerCase())).length;
      if (shared > score) { best = c; score = shared; }
    });
    return { ...m, source_cluster_name: best?.name || null, source_cluster_id: best?.id || null };
  };

  return (
    <div style={{ position: "fixed", inset: 0, overflow: "hidden" }}>
      <div style={nav}>
        <Link to="/" style={{ textDecoration: "none", minWidth: 0 }}>
          <span className="cs-heading" style={{ fontSize: 14, color: "var(--cs-bone)" }}>COGNITIVE SPACE</span>
          <span className="cs-mono cs-brand-subtitle" style={{ fontSize: 9, color: "var(--cs-mute)", marginLeft: 10 }}>ARTPART</span>
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={openInfo} className={!infoSeen ? "cs-info-pulse" : ""} style={iconButton} aria-label="Was ist Cognitive Space?">
            <Info size={15} />
          </button>
          <Link to="/admin" data-testid="nav-admin-link" className="cs-mono" style={{ fontSize: 10, color: "var(--cs-mute)", textDecoration: "none", letterSpacing: "0.2em" }}>ADMIN</Link>
        </div>
      </div>

      {graph && <SpaceStage graph={graph} pulse={pulse} onSelectCluster={(c) => setClusterId(c.id)} onSelectManifestation={(m) => setManifestation(enrichManifestation(m))} />}

      {!entered && (
        <div data-testid="public-entry-screen" className="cs-entry-overlay" style={entryOverlay}>
          <div className="cs-fadein cs-entry-shell">
            <div className="cs-entry-intro">
              <div className="cs-mono cs-entry-kicker">ARTPART · GEDANKENRAUM</div>
              <h1 className="cs-heading cs-entry-title">COGNITIVE<br />SPACE</h1>
              <p className="cs-entry-copy">Ein lebendiges digitales Kunstwerk aus Gedanken. Einzelne Stimmen bleiben anonym und verdichten sich zu Themen, Beziehungen und neuen Manifestationen.</p>
              <div className="cs-mono cs-entry-flow">GEDANKE → RESONANZ → CLUSTER → MANIFESTATION</div>
            </div>
            <div className="cs-entry-composer">
              <ThoughtComposer prompt="Teile einen Gedanken mit dem Kollektiv." onSubmitted={onSubmitted} onEnter={() => setEntered(true)} />
            </div>
            <button className="cs-entry-info-link" onClick={openInfo}>Was passiert hier?</button>
          </div>
        </div>
      )}

      {entered && <div className="cs-space-hint"><span className="cs-mono">ZIEHEN · PINCH-ZOOM · ANTIPPEN</span><span>Der Raum ordnet sich aus den tatsächlichen Beiträgen neu.</span></div>}

      {entered && !composerOpen && <button data-testid="open-composer-button" onClick={() => setComposerOpen(true)} style={fab}><Plus size={16} /> <span className="cs-mono" style={{ fontSize: 10, marginLeft: 8 }}>GEDANKE</span></button>}

      {entered && composerOpen && <div style={composerOverlay}><div className="cs-fadein" style={{ width: "100%", maxWidth: 600, padding: "0 24px", position: "relative" }}><button onClick={() => setComposerOpen(false)} style={{ ...closeBtn, position: "absolute", top: -50, right: 24 }}><X size={18} /></button><ThoughtComposer prompt="Teile einen weiteren Gedanken mit dem Kollektiv." onSubmitted={onSubmitted} /></div></div>}

      {aboutOpen && <div className="cs-about-card cs-fadein"><button onClick={() => setAboutOpen(false)} style={{ ...closeBtn, position: "absolute", top: 12, right: 12 }}><X size={16} /></button><div className="cs-mono" style={{ fontSize: 9, color: "var(--cs-amber)", marginBottom: 12 }}>WAS PASSIERT HIER?</div><p>Jeder akzeptierte Gedanke wird semantisch gelesen und mit verwandten Gedanken verbunden. Daraus entstehen Cluster. Wenn ein Cluster genügend Dichte, Wiederholung und Spannung erreicht, wird eine Manifestation vorbereitet.</p><p>Eine Manifestation ist kein Reset. Der Cluster lebt danach weiter und kann sich verändern. Später kann aus demselben Themenraum erneut eine andere Manifestation entstehen.</p><p>Das finale Bild bleibt künstlerisch kuratiert und wird von ArtPart freigegeben.</p></div>}

      <ClusterDrawer clusterId={clusterId} onClose={() => setClusterId(null)} onSelectCluster={(c) => setClusterId(c.id)} />
      <ManifestationModal manifestation={manifestation} onClose={() => setManifestation(null)} onResonated={() => loadGraph()} />
    </div>
  );
}

const nav = { position: "absolute", top: 0, left: 0, right: 0, minHeight: 56, zIndex: 40, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 18px", background: "rgba(7,7,6,0.78)", backdropFilter: "blur(14px)", borderBottom: "1px solid var(--cs-border)" };
const entryOverlay = { position: "absolute", inset: 0, zIndex: 20, display: "flex", alignItems: "center", justifyContent: "center", overflowY: "auto", background: "radial-gradient(ellipse at center, rgba(10,10,9,0.78) 0%, rgba(6,6,5,0.95) 100%)", backdropFilter: "blur(2px)" };
const composerOverlay = { position: "absolute", inset: 0, zIndex: 45, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(6,6,5,0.9)", backdropFilter: "blur(5px)" };
const fab = { position: "absolute", bottom: 24, left: 20, zIndex: 25, display: "flex", alignItems: "center", padding: "12px 18px", background: "var(--cs-bone)", color: "#0A0A09", border: "none", cursor: "pointer" };
const closeBtn = { background: "rgba(6,6,5,0.7)", border: "1px solid var(--cs-border)", color: "var(--cs-bone)", width: 36, height: 36, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" };
const iconButton = { ...closeBtn, width: 34, height: 34, borderRadius: "50%" };
